import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';
import {
  useTikTokFetch,
  Order,
  formatCurrency,
  formatDate,
} from '../hooks/useTikTokFetch';
import '../styles/pages.css';

const STATUS_OPTIONS = ['all', 'pending', 'shipped', 'delivered'] as const;

export default function Orders() {
  const { get, getShopId } = useTikTokFetch();
  const shopId = getShopId();
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 20;

  const params = new URLSearchParams({ limit: String(limit), offset: String(page * limit) });
  if (statusFilter !== 'all') params.set('status', statusFilter);

  const { data: orders = [], isLoading } = useQuery<Order[]>({
    queryKey: ['orders', shopId, statusFilter, page],
    queryFn: () => get(`/api/shop/${shopId}/orders?${params}`),
    enabled: !!shopId,
  });

  const statusClass = (status: string) => {
    const map: Record<string, string> = {
      delivered: 'badge--success',
      shipped: 'badge--primary',
      pending: 'badge--warning',
    };
    return `badge ${map[status] || ''}`;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Orders</h1>
        <div className="filter-row">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              className={`filter-btn ${statusFilter === s ? 'filter-btn--active' : ''}`}
              onClick={() => { setStatusFilter(s); setPage(0); }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="loading-spinner" />
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <ShoppingCart size={48} className="text-muted" />
          <p>No orders found</p>
        </div>
      ) : (
        <motion.div className="table-wrap" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Status</th>
                <th>Product</th>
                <th>Qty</th>
                <th>Amount</th>
                <th>Commission</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td className="mono text-sm">{o.tiktok_order_id}</td>
                  <td><span className={statusClass(o.order_status)}>{o.order_status}</span></td>
                  <td>{o.product_title || '—'}</td>
                  <td>{o.quantity}</td>
                  <td>{o.total_amount !== null ? formatCurrency(o.total_amount, o.currency) : '—'}</td>
                  <td className="text-accent">{formatCurrency(o.commission_amount, o.currency)}</td>
                  <td className="text-secondary">{formatDate(o.order_created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      {orders.length >= limit && (
        <div className="pagination">
          <button className="btn btn--sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
            Previous
          </button>
          <span className="text-secondary">Page {page + 1}</span>
          <button className="btn btn--sm" onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
